import { Box, Button, Chip, Slider, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import env from "shared/config/env";
import { randomA } from "shared/palettes";
import JoinRoom from "./components/JoinRoom";
import useChessSocket from "./hooks/useChessSocket";

const DEFAULT_SETTINGS = { rows: 7, maxLen: 3, numPlayers: 2 };
const initGs = () => ({ edgeMap: {}, tris: [], cur: 1, over: false });

const ALL_P = {
  1: { label: "ผู้เล่น 1", color: "#1565c0", fill: "#2196f3", bg: "#e3f2fd" },
  2: { label: "ผู้เล่น 2", color: "#2e7d32", fill: "#4caf50", bg: "#e8f5e9" },
  3: { label: "ผู้เล่น 3", color: "#e65100", fill: "#ff9800", bg: "#fff3e0" },
  4: { label: "ผู้เล่น 4", color: "#6a1b9a", fill: "#ab47bc", bg: "#f3e5f5" },
};

const CELL = 62;
const CELL2 = CELL * CELL;
const RH = CELL * Math.sqrt(3) / 2;
const PAD = 38;
const BG_PAD = 32;

const STEP_FNS = [
  (q, r) => [q + 1, r],
  (q, r) => [q - 1, r],
  (q, r) => [q, r + 1],
  (q, r) => [q, r - 1],
  (q, r) => [q + 1, r - 1],
  (q, r) => [q - 1, r + 1],
];

const buildGridData = (rows) => {
  const N = Math.floor(rows / 2);
  const inHex = (q, r) => Math.abs(q) <= N && Math.abs(r) <= N && Math.abs(q + r) <= N;
  const cx = PAD + N * CELL;
  const cy = PAD + N * RH;
  const svgW = cx * 2;
  const svgH = cy * 2;

  const grid = [];
  for (let r = -N; r <= N; r++)
    for (let q = -N; q <= N; q++)
      if (inHex(q, r)) grid.push({ id: `${q},${r}`, x: cx + q * CELL + r * (CELL / 2), y: cy + r * RH });

  const getId = (q, r) => (inHex(q, r) ? `${q},${r}` : null);
  const getPoint = (id) => grid.find((p) => p.id === id);

  const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, -1], [-1, 1]];
  const gridEdges = [];
  const addGE = (a, b) => {
    if (!a || !b) return;
    if (!gridEdges.some((e) => (e[0] === a && e[1] === b) || (e[0] === b && e[1] === a)))
      gridEdges.push([a, b]);
  };
  grid.forEach(({ id }) => {
    const [q, r] = id.split(",").map(Number);
    DIRS.forEach(([dq, dr]) => addGE(id, getId(q + dq, r + dr)));
  });

  const hr = N * CELL + BG_PAD;
  const hexPts = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i + 60);
    return `${cx + hr * Math.cos(a)},${cy + hr * Math.sin(a)}`;
  }).join(" ");

  return { grid, gridEdges, svgW, svgH, hexPts, getId, getPoint };
};

const dist2 = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2;
const edgeKey = (a, b) => [a, b].sort().join(":");

const getSegmentEdges = (id1, id2, getId, maxLen) => {
  const [q1, r1] = id1.split(",").map(Number);
  for (const stepFn of STEP_FNS) {
    let [q, r] = [q1, r1];
    const path = [id1];
    for (let step = 0; step < maxLen; step++) {
      const [nq, nr] = stepFn(q, r);
      const nid = getId(nq, nr);
      if (!nid) break;
      path.push(nid);
      q = nq; r = nr;
      if (nid === id2) return path.slice(0, -1).map((a, i) => [a, path[i + 1]]);
    }
  }
  return null;
};

const findNewTris = (id1, id2, edgeMap, claimedTris, grid, getPoint) => {
  const result = [];
  for (const pt of grid) {
    const id3 = pt.id;
    if (id3 === id1 || id3 === id2) continue;
    const k1 = edgeKey(id1, id3), k2 = edgeKey(id2, id3);
    if (edgeMap[k1] === undefined || edgeMap[k2] === undefined) continue;
    const p1 = getPoint(id1), p2 = getPoint(id2), p3 = getPoint(id3);
    const cross = (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
    if (Math.abs(cross) < 0.5) continue;
    const d12 = dist2(p1, p2), d23 = dist2(p2, p3), d13 = dist2(p1, p3);
    const eps = 1;
    if (!(Math.abs(d12 - CELL2) < eps && Math.abs(d23 - CELL2) < eps && Math.abs(d13 - CELL2) < eps)) continue;
    const sorted = [id1, id2, id3].sort().join(",");
    if (claimedTris.some((t) => t.ids.slice().sort().join(",") === sorted)) continue;
    result.push([id1, id2, id3]);
  }
  return result;
};

/* ── Lobby screen (waiting room + settings) ── */
function LobbyScreen({ players, pending, onChange, isHost, onStart, socketRef }) {
  const cappedNumPlayers = Math.min(pending.numPlayers, Math.max(2, players.length));
  const effectivePending = { ...pending, numPlayers: cappedNumPlayers };

  return (
    <Box sx={{ maxWidth: 460, mx: "auto", px: 2, py: 3 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box sx={{ fontWeight: "bold", fontSize: "1.4em" }}>⬡ Chain Triangle Chess</Box>
        <Box sx={{ fontSize: "0.82em", color: "#888", mt: 0.5 }}>รอผู้เล่น / ตั้งค่าเกม</Box>
      </Box>

      {/* Player list */}
      <Box sx={{ mb: 3, p: 2, background: "#f5f5f5", borderRadius: 2 }}>
        <Box sx={{ fontWeight: "bold", fontSize: "0.82em", color: "#555", mb: 1 }}>
          ผู้เล่นในห้อง ({players.length} คน)
        </Box>
        {players.length === 0 && (
          <Box sx={{ fontSize: "0.8em", color: "#aaa" }}>ยังไม่มีผู้เล่น…</Box>
        )}
        {players.map((p, i) => (
          <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={i === 0 ? "HOST" : `P${i + 1}`}
              sx={{ fontSize: "0.65em", bgcolor: i === 0 ? "#1565c0" : "#e0e0e0", color: i === 0 ? "#fff" : "#555" }}
            />
            <Box sx={{ fontSize: "0.88em", fontWeight: p.id === socketRef.current?.id ? "bold" : "normal" }}>
              {p.name}
              {p.id === socketRef.current?.id && " (คุณ)"}
            </Box>
          </Box>
        ))}
      </Box>

      {isHost ? (
        <>
          {/* Board size */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85em", mb: 1, color: "#333" }}>ขนาดกระดาน</Box>
            <ToggleButtonGroup exclusive fullWidth size="small"
              value={pending.rows}
              onChange={(_, v) => v && onChange({ ...pending, rows: v })}
            >
              {[{ v: 3, label: "เล็ก (3)" }, { v: 5, label: "กลาง (5)" }, { v: 7, label: "ใหญ่ (7)" }, { v: 9, label: "ใหญ่มาก (9)" }].map(({ v, label }) => (
                <ToggleButton key={v} value={v} sx={{ fontSize: "0.78em", py: 0.75 }}>{label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Max rubber band length */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85em", mb: 0.5, color: "#333" }}>
              ความยาวสูงสุดของหนังยาง:{" "}
              <Box component="span" sx={{ color: "#1565c0" }}>{pending.maxLen} ช่อง</Box>
            </Box>
            <Slider min={1} max={10} step={1} marks valueLabelDisplay="auto"
              value={pending.maxLen}
              onChange={(_, v) => onChange({ ...pending, maxLen: v })}
              sx={{ color: "#1565c0" }}
            />
          </Box>

          {/* Number of players */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85em", mb: 1, color: "#333" }}>
              จำนวนผู้เล่น{" "}
              {players.length < pending.numPlayers && (
                <Box component="span" sx={{ color: "#e65100", fontSize: "0.82em" }}>
                  (รอผู้เล่นอีก {pending.numPlayers - players.length} คน)
                </Box>
              )}
            </Box>
            <ToggleButtonGroup exclusive fullWidth size="small"
              value={pending.numPlayers}
              onChange={(_, v) => v && onChange({ ...pending, numPlayers: v })}
            >
              {[2, 3, 4].map((v) => (
                <ToggleButton key={v} value={v} sx={{ fontSize: "0.82em" }}>{v} คน</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={players.length < 2}
            onClick={() => onStart(effectivePending)}
            sx={{ bgcolor: "#1565c0" }}
          >
            {players.length < 2 ? "รอผู้เล่นอีกคน…" : "เริ่มเกม"}
          </Button>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 3, color: "#888", fontSize: "0.9em" }}>
          รอ host เริ่มเกม…
        </Box>
      )}
    </Box>
  );
}

/* ── Main component ── */
export default function Chess({ duration }) {
  const [inputCode, setInputCode] = useState(env.defaultRoomCode);
  const [userName, setUserName] = useState(
    () => randomA[Math.floor(Math.random() * randomA.length)]
  );
  const [pending, setPending] = useState(DEFAULT_SETTINGS);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState("");

  const {
    isJoined, players, gameStarted, settings, gs, myPlayerNum,
    socketRef, joinRoom, startGame, placeEdge, resetGame, backToLobby, exitRoom,
  } = useChessSocket(inputCode, userName);

  const activeSettings = gameStarted ? settings : pending;

  const { grid, gridEdges, svgW, svgH, hexPts, getId, getPoint } = useMemo(
    () => buildGridData(activeSettings?.rows ?? DEFAULT_SETTINGS.rows),
    [activeSettings?.rows]
  );

  const P = useMemo(
    () => Object.fromEntries(
      Array.from({ length: activeSettings?.numPlayers ?? 2 }, (_, i) => [i + 1, ALL_P[i + 1]])
    ),
    [activeSettings?.numPlayers]
  );

  const isHost = players[0]?.id === socketRef.current?.id;
  const isMyTurn = gs && myPlayerNum > 0 && gs.cur === myPlayerNum;

  const scores = gs
    ? gs.tris.reduce(
        (a, t) => { a[t.player] = (a[t.player] || 0) + 1; return a; },
        Object.fromEntries(Array.from({ length: settings?.numPlayers ?? 2 }, (_, i) => [i + 1, 0]))
      )
    : {};

  const handleJoinRoom = () => { if (inputCode) joinRoom(); };

  const handleStartGame = (s) => startGame(s);

  const onPt = (pt) => {
    if (!gs || gs.over || !isMyTurn) return;

    if (!sel) { setSel(pt.id); setErr(""); return; }
    if (sel === pt.id) { setSel(null); setErr(""); return; }

    const segEdges = getSegmentEdges(sel, pt.id, getId, settings.maxLen);
    if (!segEdges) {
      setErr(`วางได้เฉพาะแนว A/B/C ยาว 1–${settings.maxLen} ช่อง`);
      setSel(null);
      return;
    }

    for (const [a, b] of segEdges) {
      if (gs.edgeMap[edgeKey(a, b)] !== undefined) {
        setErr("เส้นซ้อนทับเส้นเดิม — ห้ามแม้บางส่วน");
        setSel(null);
        return;
      }
    }

    const newEdgeMap = { ...gs.edgeMap };
    for (const [a, b] of segEdges) newEdgeMap[edgeKey(a, b)] = gs.cur;

    let allTris = [...gs.tris];
    let totalNewTris = 0;
    for (const [a, b] of segEdges) {
      const newTriIds = findNewTris(a, b, newEdgeMap, allTris, grid, getPoint);
      const newTrisForEdge = newTriIds.map((ids) => ({ player: gs.cur, ids }));
      allTris = [...allTris, ...newTrisForEdge];
      totalNewTris += newTrisForEdge.length;
    }

    const gameOver = Object.keys(newEdgeMap).length >= gridEdges.length;
    const gotBonus = totalNewTris > 0;
    const nextPlayer = gameOver ? gs.cur : (gs.cur % settings.numPlayers) + 1;

    setSel(null);
    setErr(gotBonus ? `🎉 +${totalNewTris} แต้ม!` : "");

    placeEdge(newEdgeMap, allTris, nextPlayer, gameOver);
  };

  const edgeMap = gs?.edgeMap ?? {};
  const tris = gs?.tris ?? [];
  const cur = gs?.cur ?? 1;
  const over = gs?.over ?? false;
  const edgesLeft = gridEdges.length - Object.keys(edgeMap).length;

  const winner = over
    ? (() => {
        const maxScore = Math.max(...Object.values(scores));
        const tied = Object.keys(scores).filter((p) => scores[p] === maxScore);
        return tied.length === 1 ? Number(tied[0]) : 0;
      })()
    : null;

  /* ── Render: Join ── */
  if (!isJoined) {
    return (
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 10, opacity: 0 }}
        transition={{ duration }}
      >
        <JoinRoom
          inputCode={inputCode}
          setInputCode={setInputCode}
          userName={userName}
          setUserName={setUserName}
          handleJoinRoom={handleJoinRoom}
        />
      </motion.div>
    );
  }

  /* ── Render: Lobby ── */
  if (!gameStarted) {
    return (
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 10, opacity: 0 }}
        transition={{ duration }}
      >
        <LobbyScreen
          players={players}
          pending={pending}
          onChange={setPending}
          isHost={isHost}
          onStart={handleStartGame}
          socketRef={socketRef}
        />
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Button size="small" variant="text" color="inherit" onClick={exitRoom}
            sx={{ fontSize: "0.72em", opacity: 0.45 }}>
            ออกจากห้อง
          </Button>
        </Box>
      </motion.div>
    );
  }

  /* ── Render: Game ── */
  return (
    <motion.div
      initial={{ x: -10, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 10, opacity: 0 }}
      transition={{ duration }}
    >
      <Box sx={{ maxWidth: Math.max(480, svgW + 60), mx: "auto", px: 2, py: 2 }}>
        {/* Title + room info */}
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          <Box sx={{ fontWeight: "bold", fontSize: "1.25em" }}>⬡ Chain Triangle Chess</Box>
          <Box sx={{ fontSize: "0.75em", color: "#888", mt: 0.25 }}>
            ห้อง: <b>{inputCode}</b> · {settings.rows} แถว · เส้นยาวสูงสุด {settings.maxLen} · {settings.numPlayers} ผู้เล่น
          </Box>
          {myPlayerNum > 0 ? (
            <Box sx={{ fontSize: "0.78em", color: P[myPlayerNum]?.color, fontWeight: "bold", mt: 0.25 }}>
              คุณ = {P[myPlayerNum]?.label}
            </Box>
          ) : (
            <Box sx={{ fontSize: "0.78em", color: "#aaa", mt: 0.25 }}>ผู้ชม</Box>
          )}
        </Box>

        {/* Score row */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          {Array.from({ length: settings.numPlayers }, (_, i) => i + 1).map((p) => (
            <Box key={p} sx={{
              flex: 1, textAlign: "center", py: 1, px: 0.5, borderRadius: 2,
              border: `2px solid ${P[p].fill}`,
              background: cur === p && !over ? P[p].bg : "transparent",
              transition: "background 0.3s",
            }}>
              <Box sx={{ fontWeight: "bold", fontSize: "0.75em", color: P[p].color }}>
                {cur === p && !over ? "▶ " : ""}{P[p].label}
              </Box>
              <Box sx={{ fontSize: "1em", fontWeight: "bold" }}>{scores[p] || 0} แต้ม</Box>
            </Box>
          ))}
        </Stack>

        {/* Progress */}
        {!over && (
          <Box sx={{ textAlign: "center", fontSize: "0.72em", color: "#999", mb: 0.5 }}>
            เส้นที่เหลือ: {edgesLeft} / {gridEdges.length}
          </Box>
        )}

        {/* Board SVG */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <svg width="100%" viewBox={`${-BG_PAD} ${-BG_PAD} ${svgW + BG_PAD * 2} ${svgH + BG_PAD * 2}`}
            style={{ maxWidth: svgW + BG_PAD * 2, cursor: isMyTurn && !over ? "crosshair" : "default" }}>
            <polygon points={hexPts} fill="#0d0d0d" stroke="#222" strokeWidth="3" />

            {gridEdges.map(([id1, id2]) => {
              const p1 = getPoint(id1), p2 = getPoint(id2);
              return <line key={`ge-${id1}-${id2}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke="#2a2a2a" strokeWidth="1.5" />;
            })}

            {Object.entries(edgeMap).map(([key, player]) => {
              const [id1, id2] = key.split(":");
              const p1 = getPoint(id1), p2 = getPoint(id2);
              return <line key={`de-${key}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={P[player]?.fill} strokeWidth="2.5" strokeLinecap="round" opacity={0.85} />;
            })}

            {tris.map((t, i) => {
              const [pa, pb, pc] = t.ids.map(getPoint);
              const cx = (pa.x + pb.x + pc.x) / 3;
              const cy = (pa.y + pb.y + pc.y) / 3;
              return (
                <g key={`tri-${i}`}>
                  <polygon points={`${pa.x},${pa.y} ${pb.x},${pb.y} ${pc.x},${pc.y}`}
                    fill={P[t.player]?.fill} fillOpacity={0.28} />
                  <circle cx={cx} cy={cy} r={9} fill={P[t.player]?.fill}
                    stroke={P[t.player]?.color} strokeWidth={1.5} />
                  <polygon points={`${cx},${cy - 6} ${cx - 5},${cy + 4} ${cx + 5},${cy + 4}`}
                    fill="rgba(255,255,255,0.5)" />
                </g>
              );
            })}

            {grid.map((pt) => {
              const isSel = sel === pt.id;
              return (
                <g key={pt.id} onClick={() => onPt(pt)}
                  style={{ cursor: isMyTurn && !over ? "pointer" : "default" }}>
                  <circle cx={pt.x} cy={pt.y} r={18} fill="transparent" />
                  {isSel && <circle cx={pt.x} cy={pt.y} r={13}
                    fill="none" stroke="#fff" strokeWidth="2" strokeDasharray="4 3" opacity={0.9} />}
                  <circle cx={pt.x} cy={pt.y} r={7}
                    fill={isSel ? "#fff" : "#3a3a3a"}
                    stroke={isSel ? "#fff" : "#555"} strokeWidth={1.5} />
                </g>
              );
            })}
          </svg>
        </Box>

        {/* Feedback */}
        {err && (
          <Box sx={{
            textAlign: "center",
            color: err.startsWith("🎉") ? "#2e7d32" : "#c62828",
            fontSize: "0.82em", mb: 0.5, minHeight: 20,
          }}>
            {err}
          </Box>
        )}

        {/* Turn indicator / game over */}
        {!over ? (
          <Box sx={{ textAlign: "center", fontWeight: "bold", mb: 1, fontSize: "0.95em",
            color: P[cur]?.color }}>
            {isMyTurn
              ? sel
                ? `${P[cur]?.label} (คุณ) — คลิกปลายทาง (ยาว 1–${settings.maxLen} ช่อง)`
                : `${P[cur]?.label} (คุณ) — คลิกจุดเริ่มต้น`
              : `รอ ${P[cur]?.label}…`}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ fontSize: "1.6em", fontWeight: "bold", mb: 0.5 }}>
              {winner === 0 ? "🤝 เสมอ!" : `🎉 ${P[winner]?.label} ชนะ!`}
            </Box>
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 2, flexWrap: "wrap" }}>
              {Array.from({ length: settings.numPlayers }, (_, i) => i + 1).map((p) => (
                <Box key={p} sx={{ fontSize: "0.85em", color: P[p].color, fontWeight: "bold" }}>
                  {P[p].label}: {scores[p] || 0}
                </Box>
              ))}
            </Stack>
            {isHost && (
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="contained" sx={{ bgcolor: "#1565c0" }} onClick={resetGame}>
                  เล่นใหม่
                </Button>
                <Button variant="outlined" color="inherit" onClick={backToLobby}>
                  ตั้งค่าใหม่
                </Button>
              </Stack>
            )}
            {!isHost && (
              <Box sx={{ fontSize: "0.82em", color: "#888" }}>รอ host เริ่มเกมใหม่…</Box>
            )}
          </Box>
        )}

        {!over && isHost && (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button size="small" variant="text" color="inherit" onClick={backToLobby}
              sx={{ fontSize: "0.72em", opacity: 0.45 }}>
              ตั้งค่า / เริ่มใหม่
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Button size="small" variant="text" color="inherit" onClick={exitRoom}
            sx={{ fontSize: "0.72em", opacity: 0.35 }}>
            ออกจากห้อง
          </Button>
        </Box>

        {/* Rules */}
        <Box sx={{
          mt: 2, p: 1.5, background: "#f5f5f5", borderRadius: 2,
          fontSize: "0.73em", color: "#666", lineHeight: 1.7,
        }}>
          <Box sx={{ fontWeight: "bold", mb: 0.5, color: "#333" }}>กติกา (Chain Triangle Chess)</Box>
          <Box>• ผลัดกันขึงเส้น <b>1 เส้นต่อเทิร์น</b> บนเส้นว่างในกริด (แนว A/B/C เท่านั้น)</Box>
          <Box>• ขึงได้ยาว <b>1–{settings.maxLen} ช่อง</b> ในแนวตรงเดียวกัน — ห้ามซ้อนทับแม้บางส่วน</Box>
          <Box>• ถ้าสร้าง <b>สามเหลี่ยมย่อยครบ</b> → วางหมาก +1 แต้ม (ไม่ได้โบนัสเทิร์น)</Box>
          <Box>• <b>เกมจบเมื่อเส้นทุกเส้นถูกขึงหมด</b> — นับหมาก มากกว่า คือผู้ชนะ</Box>
        </Box>
      </Box>
    </motion.div>
  );
}
